import { store } from '../stateManager/store';

type MscStorageApi = {
  set: (
    key: string,
    value: unknown,
    options?: { ttlDays?: number; enforceConsent?: boolean }
  ) => boolean;
  get: <T = unknown>(key: string) => T | null;
  remove: (key: string) => void;
};

type WebStorageType = 'local' | 'session';

/**
 * Same flag as `getCookieComplianceEnabled` in contextAwareUtils.
 * try/catch: during store bootstrap (e.g. genericConfigSlice initialState → mscGet), `store` may not be ready yet.
 */
function isCookieComplianceEnabledSafe(): boolean {
  try {
    return (
      store.getState().genericConfigs?.configs?.b2cCommerceConfig?.cookieComplianceEnabled ?? false
    );
  } catch {
    return false;
  }
}

function getMscStorageApi(): MscStorageApi | null {
  try {
    // When cookie compliance is off (AEM), use plain session/local storage only — same as pre-compliance.
    if (!isCookieComplianceEnabledSafe()) return null;
    const w = window as unknown as { __mscStorage?: Partial<MscStorageApi> };
    const api = w.__mscStorage;
    if (!api) return null;
    if (typeof api.set !== 'function') return null;
    if (typeof api.get !== 'function') return null;
    if (typeof api.remove !== 'function') return null;
    return api as MscStorageApi;
  } catch {
    return null;
  }
}

function getWebStorage(storage: WebStorageType): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return storage === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

export function mscSet(
  storage: WebStorageType,
  key: string,
  value: unknown,
  ttlDays?: number
): boolean {
  const api = getMscStorageApi();
  if (api) {
    const ok =
      typeof ttlDays === 'number'
        ? api.set(key, value, { ttlDays })
        : api.set(key, value, undefined);
    if (ok) return true;
    // If storage-compliance is present but refuses the write (no config/consent/ttl),
    // fall back to previous raw storage behavior so the app doesn't break.
  }

  // Fallback: keep previous behavior when compliance MFE isn't present.
  const webStorage = getWebStorage(storage);
  if (!webStorage) return false;
  try {
    if (typeof value === 'string') {
      webStorage.setItem(key, value);
    } else {
      webStorage.setItem(key, JSON.stringify(value));
    }
    return true;
  } catch {
    return false;
  }
}

export function mscGet<T = unknown>(storage: WebStorageType, key: string): T | null {
  const api = getMscStorageApi();
  if (api) return api.get<T>(key);

  const webStorage = getWebStorage(storage);
  if (!webStorage) return null;
  try {
    const raw = webStorage.getItem(key);
    if (raw === null) return null;
    try {
      const parsed = JSON.parse(raw) as any;
      if (
        parsed &&
        typeof parsed === 'object' &&
        typeof parsed._exp === 'number' &&
        typeof parsed._ts === 'number' &&
        'value' in parsed
      ) {
        return parsed.value as T;
      }
      return parsed as T;
    } catch {
      return raw as unknown as T;
    }
  } catch {
    return null;
  }
}

export function mscRemove(storage: WebStorageType, key: string): void {
  const api = getMscStorageApi();
  if (api) api.remove(key);
  const webStorage = getWebStorage(storage);
  if (!webStorage) return;
  try {
    webStorage.removeItem(key);
  } catch {
    // ignore
  }
}
